/**
 * Minimal Web Audio "juice" — short synthesized blips for drops and results.
 * Entirely client-side (no assets, no network). Safe no-op where the Web Audio
 * API is unavailable, and respects the on/off toggle.
 *
 * Reliability notes (why this is more than a few oscillators):
 *  - Browsers start an AudioContext "suspended" until a user gesture resumes it.
 *    `unlock()` is meant to be called from the very first pointer/key event so the
 *    context is already "running" before any sound is scheduled — this matters for
 *    the AI-moves-first case, where the first drop sound fires from a timer, not a
 *    click, and would otherwise be silently dropped.
 *  - Everything routes through a master gain so the on/off toggle is instant and
 *    levels stay consistent.
 */
type Ctor = typeof AudioContext;

export class Sound {
  private ctx: AudioContext | null = null;
  private master: GainNode | null = null;
  private _enabled = true;

  get enabled(): boolean {
    return this._enabled;
  }

  setEnabled(on: boolean): void {
    this._enabled = on;
    const ctx = this.ensure();
    if (ctx && this.master) this.master.gain.setValueAtTime(on ? MASTER_GAIN : 0, ctx.currentTime);
    if (on) this.resume();
  }

  /** Create the context + master gain. Cheap and idempotent; returns null if unsupported. */
  private ensure(): AudioContext | null {
    try {
      if (!this.ctx) {
        const Ctx: Ctor | undefined =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext;
        if (!Ctx) return null;
        this.ctx = new Ctx();
        this.master = this.ctx.createGain();
        this.master.gain.value = this._enabled ? MASTER_GAIN : 0;
        this.master.connect(this.ctx.destination);
      }
      return this.ctx;
    } catch {
      return null;
    }
  }

  private resume(): void {
    const ctx = this.ensure();
    if (ctx && ctx.state === 'suspended') void ctx.resume();
  }

  /**
   * Unlock audio from a user gesture (autoplay policy). Creates + resumes the
   * context and plays a silent tick to fully warm up the graph. Call once on the
   * first pointerdown/keydown.
   */
  unlock(): void {
    const ctx = this.ensure();
    if (!ctx) return;
    if (ctx.state === 'suspended') void ctx.resume();
    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      gain.gain.value = 0;
      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.02);
    } catch {
      /* ignore */
    }
  }

  /** One enveloped tone, optionally sweeping from `freq` to `freqEnd`. */
  private blip(
    freq: number,
    freqEnd: number,
    start: number,
    duration: number,
    type: OscillatorType,
    peak: number,
  ): void {
    if (!this._enabled) return;
    const ctx = this.ensure();
    if (!ctx || !this.master) return;
    if (ctx.state === 'suspended') void ctx.resume();

    const t0 = ctx.currentTime + start + 0.005; // never schedule exactly "now"
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = type;
    osc.frequency.setValueAtTime(freq, t0);
    if (freqEnd > 0 && freqEnd !== freq) {
      osc.frequency.exponentialRampToValueAtTime(freqEnd, t0 + duration);
    }
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + 0.015);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);
    osc.connect(gain).connect(this.master);
    osc.start(t0);
    osc.stop(t0 + duration + 0.03);
  }

  /** A satisfying "plock" as a disc lands: a quick downward sweep + low thump. */
  drop(): void {
    this.blip(520, 170, 0, 0.18, 'triangle', 0.5);
    this.blip(150, 90, 0.01, 0.2, 'sine', 0.4);
  }

  /** Rising arpeggio for a win. */
  win(): void {
    [523, 659, 784, 1047].forEach((f, i) => this.blip(f, 0, i * 0.1, 0.26, 'triangle', 0.45));
  }

  /** Descending tones for a loss. */
  lose(): void {
    [392, 330, 262].forEach((f, i) => this.blip(f, 0, i * 0.13, 0.32, 'sine', 0.4));
  }

  /** Neutral two-note for a draw. */
  draw(): void {
    [440, 440].forEach((f, i) => this.blip(f, 0, i * 0.16, 0.22, 'triangle', 0.35));
  }
}

/** Master output level (0–1). Keeps the blips audible but gentle. */
const MASTER_GAIN = 0.45;
